import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  PAYROLL_DEPARTMENTS,
  PAYROLL_DEPARTMENT_LABELS,
  type PayrollDepartment,
} from '../../../types/finances';
import {
  JOB_EMPLOYMENT_TYPES,
  JOB_EMPLOYMENT_TYPE_LABELS,
  JOB_POSTING_STATUSES,
  JOB_POSTING_STATUS_LABELS,
  type JobEmploymentType,
  type JobPosting,
  type JobPostingStatus,
} from '../../../types/hiring';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import {
  createJobPosting,
  updateJobPosting,
  type JobPostingInput,
} from '../../../utils/hiringFirestore';

type Props = {
  posting: JobPosting | null;
  prefill?: {
    payrollPositionId?: string;
    title?: string;
    department?: string;
  };
  onClose: () => void;
};

const JobPostingEditor: React.FC<Props> = ({ posting, prefill, onClose }) => {
  const isEdit = !!posting?.id;
  const [title, setTitle] = useState(posting?.title || prefill?.title || '');
  const [department, setDepartment] = useState<string>(
    posting?.department || prefill?.department || 'shop',
  );
  const [location, setLocation] = useState(posting?.location || 'Chesterfield, VA');
  const [employmentType, setEmploymentType] = useState<JobEmploymentType>(
    posting?.employmentType || 'full-time',
  );
  const [description, setDescription] = useState(posting?.description || '');
  const [requirements, setRequirements] = useState(posting?.requirements || '');
  const [status, setStatus] = useState<JobPostingStatus>(posting?.status || 'draft');
  const [payrollPositionId, setPayrollPositionId] = useState<string>(
    posting?.payrollPositionId || prefill?.payrollPositionId || '',
  );
  const [payrollOptions, setPayrollOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'payrollPositions'),
            where('status', 'in', ['open', 'planned']),
          ),
        );
        setPayrollOptions(
          snap.docs.map(d => {
            const data = d.data();
            const dept = PAYROLL_DEPARTMENT_LABELS[data.department as PayrollDepartment] || data.department;
            return {
              id: d.id,
              label: `${data.title || 'Untitled'} (${dept})`,
            };
          }),
        );
      } catch {
        setPayrollOptions([]);
      }
    })();
  }, []);

  const payload = useMemo<JobPostingInput>(() => ({
    title,
    department,
    location,
    employmentType,
    description,
    requirements,
    status,
    payrollPositionId: payrollPositionId || null,
  }), [title, department, location, employmentType, description, requirements, status, payrollPositionId]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit && posting) {
        await updateJobPosting(posting.id, payload);
      } else {
        await createJobPosting(payload);
      }
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Failed to save posting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="people-hiring-modal-backdrop" onClick={onClose}>
      <div className="people-hiring-modal" onClick={e => e.stopPropagation()}>
        <div className="people-hiring-modal-header">
          <h3>{isEdit ? 'Edit job posting' : 'New job posting'}</h3>
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="people-hiring-modal-body">
          {error && <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>}
          <div className="people-hiring-field">
            <label>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Department</label>
              <select value={department} onChange={e => setDepartment(e.target.value)}>
                {PAYROLL_DEPARTMENTS.map(d => (
                  <option key={d} value={d}>{PAYROLL_DEPARTMENT_LABELS[d]}</option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>
            <div className="people-hiring-field">
              <label>Employment type</label>
              <select
                value={employmentType}
                onChange={e => setEmploymentType(e.target.value as JobEmploymentType)}
              >
                {JOB_EMPLOYMENT_TYPES.map(t => (
                  <option key={t} value={t}>{JOB_EMPLOYMENT_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Location</label>
              <input value={location} onChange={e => setLocation(e.target.value)} />
            </div>
            <div className="people-hiring-field">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value as JobPostingStatus)}>
                {JOB_POSTING_STATUSES.map(s => (
                  <option key={s} value={s}>{JOB_POSTING_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="people-hiring-field">
            <label>Link to payroll position (optional)</label>
            <select
              value={payrollPositionId}
              onChange={e => setPayrollPositionId(e.target.value)}
            >
              <option value="">None</option>
              {payrollOptions.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="people-hiring-field">
            <label>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="people-hiring-field">
            <label>Requirements</label>
            <textarea value={requirements} onChange={e => setRequirements(e.target.value)} />
          </div>
          <div className="people-hiring-modal-footer">
            <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="ops-new-btn" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create posting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPostingEditor;
